import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { JobChecklistPhaseKey } from "@/data/types";

export type ChecklistTemplateHeader = {
  id: string;
  organization_id: string | null;
  key: string | null;
  trade_key: string | null;
  name: string;
  is_default: boolean;
  is_active: boolean;
};

export type ChecklistTemplateItem = {
  id?: string;
  template_id?: string;
  phase_key: JobChecklistPhaseKey;
  item_index: number;
  label: string;
  proof_label: string | null;
  requires_proof: boolean;
  sort_order: number;
};

export type ChecklistTemplatePhase = {
  template_id?: string;
  phase_key: JobChecklistPhaseKey;
  label: string;
  sort_order: number;
};

export type FullChecklistTemplate = ChecklistTemplateHeader & {
  items: ChecklistTemplateItem[];
  phases: ChecklistTemplatePhase[];
};

export const TRADE_KEYS = [
  "bath", "flooring", "paint", "windows_doors",
  "roofing", "plumbing", "drywall", "general",
] as const;
export type TradeKey = typeof TRADE_KEYS[number];

// M1 — use Record<TradeKey, string> for exhaustive type safety
export const TRADE_KEY_LABELS: Record<TradeKey, string> = {
  bath: "Bath",
  flooring: "Flooring",
  paint: "Paint",
  windows_doors: "Windows & Doors",
  roofing: "Roofing",
  plumbing: "Plumbing",
  drywall: "Drywall",
  general: "General",
};

async function getRequiredOrganizationId(context: string): Promise<string | null> {
  const organizationId = await getCurrentOrganizationId();
  if (!organizationId) {
    console.warn(`[checklist-template] ${context}: missing organization_id`);
    return null;
  }
  return organizationId;
}

async function getEditableTemplateHeader(
  templateId: string,
  organizationId: string,
): Promise<ChecklistTemplateHeader | null> {
  const { data, error } = await supabase
    .from("job_checklist_templates")
    .select("id, organization_id, key, trade_key, name, is_default, is_active")
    .eq("id", templateId)
    .eq("organization_id", organizationId)
    .maybeSingle();

  if (error) {
    console.warn("[checklist-template] getEditableTemplateHeader error:", error);
    return null;
  }
  return (data as ChecklistTemplateHeader | null) ?? null;
}

export async function listChecklistTemplates(): Promise<ChecklistTemplateHeader[]> {
  const orgId = await getCurrentOrganizationId();
  let query = supabase
    .from("job_checklist_templates")
    .select("id, organization_id, key, trade_key, name, is_default, is_active")
    .order("name", { ascending: true });

  query = orgId
    ? query.or(`organization_id.is.null,organization_id.eq.${orgId}`)
    : query.is("organization_id", null);

  const { data, error } = await query;

  if (error) {
    console.warn("[checklist-template] listChecklistTemplates error:", error);
    return [];
  }

  const rows = (data ?? []) as ChecklistTemplateHeader[];
  return rows.sort((a, b) => {
    const aIsOrg = a.organization_id === orgId ? 0 : 1;
    const bIsOrg = b.organization_id === orgId ? 0 : 1;
    if (aIsOrg !== bIsOrg) return aIsOrg - bIsOrg;
    return a.name.localeCompare(b.name);
  });
}

export async function getChecklistTemplate(id: string): Promise<FullChecklistTemplate | null> {
  const { data: header, error: headerError } = await supabase
    .from("job_checklist_templates")
    .select("id, organization_id, key, trade_key, name, is_default, is_active")
    .eq("id", id)
    .maybeSingle();

  if (headerError || !header) {
    if (headerError) console.warn("[checklist-template] getChecklistTemplate error:", headerError);
    return null;
  }

  const { data: items, error: itemsError } = await supabase
    .from("job_checklist_template_items")
    .select("id, template_id, phase_key, item_index, label, proof_label, requires_proof, sort_order")
    .eq("template_id", id)
    .order("sort_order", { ascending: true })
    .order("phase_key", { ascending: true })
    .order("item_index", { ascending: true });

  if (itemsError) {
    console.warn("[checklist-template] getChecklistTemplate items error:", itemsError);
    return null;
  }

  const { data: phases, error: phasesError } = await supabase
    .from("job_checklist_template_phases")
    .select("template_id, phase_key, label, sort_order")
    .eq("template_id", id)
    .order("sort_order", { ascending: true });

  if (phasesError) console.warn("[checklist-template] getChecklistTemplate phases error:", phasesError);

  return {
    ...(header as ChecklistTemplateHeader),
    items: (items ?? []) as ChecklistTemplateItem[],
    phases: (phases ?? []) as ChecklistTemplatePhase[],
  };
}

export async function createChecklistTemplate(payload: {
  name: string;
  key: string | null;
  trade_key: string | null;
  is_default: boolean;
  is_active: boolean;
}): Promise<{ id: string } | null> {
  const organization_id = await getRequiredOrganizationId("createChecklistTemplate");
  if (!organization_id) {
    return null;
  }

  const { data, error } = await supabase
    .from("job_checklist_templates")
    .insert({ ...payload, organization_id })
    .select("id")
    .maybeSingle();

  if (error) {
    console.warn("[checklist-template] createChecklistTemplate error:", error);
    return null;
  }
  if (!data) return null;
  return data as { id: string };
}

export async function updateChecklistTemplate(
  id: string,
  patch: Partial<Pick<ChecklistTemplateHeader, "name" | "key" | "trade_key" | "is_default" | "is_active">>,
): Promise<{ error: unknown }> {
  const organization_id = await getRequiredOrganizationId("updateChecklistTemplate");
  if (!organization_id) return { error: new Error("Missing organization_id") };

  const { error } = await supabase
    .from("job_checklist_templates")
    .update(patch)
    .eq("id", id)
    .eq("organization_id", organization_id);

  if (error) console.warn("[checklist-template] updateChecklistTemplate error:", error);
  return { error };
}

/**
 * Replaces all items and phases for a template using delete-then-insert.
 * NOTE: This is not atomic — if an insert fails after a delete succeeds,
 * the template will be left with no items. Keep this behind org ownership
 * checks until a transactional RPC is available.
 */
export async function replaceTemplateItems(
  templateId: string,
  items: Omit<ChecklistTemplateItem, "id" | "template_id">[],
  phases: Omit<ChecklistTemplatePhase, "template_id">[],
): Promise<{ error: unknown }> {
  const organization_id = await getRequiredOrganizationId("replaceTemplateItems");
  if (!organization_id) return { error: new Error("Missing organization_id") };

  const editableTemplate = await getEditableTemplateHeader(templateId, organization_id);
  if (!editableTemplate) {
    return { error: new Error("Template not found or not editable for this organization") };
  }

  const { error: delItemsError } = await supabase
    .from("job_checklist_template_items")
    .delete()
    .eq("template_id", templateId);
  if (delItemsError) return { error: delItemsError };

  const { error: delPhasesError } = await supabase
    .from("job_checklist_template_phases")
    .delete()
    .eq("template_id", templateId);
  if (delPhasesError) return { error: delPhasesError };

  if (items.length > 0) {
    const { error: insertItemsError } = await supabase
      .from("job_checklist_template_items")
      .insert(items.map((item) => ({ ...item, template_id: templateId })));
    if (insertItemsError) return { error: insertItemsError };
  }

  if (phases.length > 0) {
    const { error: insertPhasesError } = await supabase
      .from("job_checklist_template_phases")
      .insert(phases.map((phase) => ({ ...phase, template_id: templateId })));
    if (insertPhasesError) return { error: insertPhasesError };
  }

  return { error: null };
}

export async function setTemplateAsDefault(
  templateId: string,
  tradeKey: TradeKey | string,
): Promise<{ error: unknown }> {
  const organization_id = await getRequiredOrganizationId("setTemplateAsDefault");
  if (!organization_id) return { error: new Error("Missing organization_id") };

  if (!TRADE_KEYS.includes(tradeKey as TradeKey)) {
    console.warn("[checklist-template] setTemplateAsDefault: invalid tradeKey", tradeKey);
    return { error: new Error(`Invalid trade key: ${tradeKey}`) };
  }

  const editableTemplate = await getEditableTemplateHeader(templateId, organization_id);
  if (!editableTemplate) {
    return { error: new Error("Template not found or not editable for this organization") };
  }

  const { error: unsetError } = await supabase
    .from("job_checklist_templates")
    .update({ is_default: false })
    .eq("organization_id", organization_id)
    .or(`key.eq.${tradeKey},trade_key.eq.${tradeKey}`)
    .neq("id", templateId);

  if (unsetError) return { error: unsetError };

  const { error: setError } = await supabase
    .from("job_checklist_templates")
    .update({ is_default: true })
    .eq("id", templateId)
    .eq("organization_id", organization_id);

  return { error: setError };
}

export async function deleteChecklistTemplate(id: string): Promise<{ error: unknown }> {
  const organization_id = await getRequiredOrganizationId("deleteChecklistTemplate");
  if (!organization_id) return { error: new Error("Missing organization_id") };

  const { error } = await supabase
    .from("job_checklist_templates")
    .update({ is_active: false })
    .eq("id", id)
    .eq("organization_id", organization_id);

  if (error) console.warn("[checklist-template] deleteChecklistTemplate error:", error);
  return { error };
}

export async function duplicateChecklistTemplate(
  sourceId: string,
): Promise<{ id: string } | null> {
  const source = await getChecklistTemplate(sourceId);
  if (!source) return null;

  const organization_id = await getRequiredOrganizationId("duplicateChecklistTemplate");
  if (!organization_id) return null;

  const newHeader = await createChecklistTemplate({
    name: `${source.name} (Copy)`,
    key: source.key,
    trade_key: source.trade_key,
    is_default: false,
    is_active: true,
  });
  if (!newHeader) return null;

  const { error } = await replaceTemplateItems(
    newHeader.id,
    source.items.map(({ id: _id, template_id: _tid, ...rest }) => rest),
    source.phases.map(({ template_id: _tid, ...rest }) => rest),
  );

  if (error) {
    console.warn("[checklist-template] duplicateChecklistTemplate: items copy failed", error);
    return null;
  }

  return newHeader;
}
